import type { WProjectCustomizationPageView } from '@zola/svc-web-api-ts-client';
import { CardType } from '../constants/cardTypes';
import { getSizesPerOrientation } from './getSizesPerOrientation';
import { Bleed, getCardBleed } from './getCardBleed';

/**
 * Get the card dimensions in points, adjusted according for bleed & folding.
 *
 * - For customization flow previews, we use the template dimensions as-is. Lines are shown around the danger zone.
 *
 * - For non-customization previews, we want to show what the final printed card will look like, so we subtract the bleed from template dimensions.
 *
 *
 * --- Important definitions ---
 *
 * BLEED: The area that WILL be cut off after printing. To ensure consistent alignment at the printers,
 * our templates includes the bleed zone around the content (i.e. the template is the size of the uncut card).
 * For cards that don't need to be cut (e.g. envelopes, napkins), the bleed will be 0pts.
 *
 * e. g. For a "Petite" sized card
 *  - Printed card dimensions: 4.25 x 5.5
 *  - Bleed value: 0.125 on each side (so 0.25 total)
 *  => Template dimensions: 4.5 x 5.75
 *
 * SAFETY AREA: An additional area that MAY be partially cut off after printing. When cards are cut, there are always
 * slight alignment differences depending on the piece of paper's position, which means that it would be possible
 * for user content to be cut off if it's too close to the edge. For this reason, we specify an additional margin
 * on each side that users should avoid placing content in.
 *
 * DANGER ZONE: The sum of bleed & safety area. Shown in the customization flow with the message "Keep important content inside this line".
 */
export const getCardDimensionsInPoints = ({
  cardType,
  pageWidthPts,
  pageHeightPts,
  isFoldable,
  pageOrientation,
  disableBleed,
  bleed,
}: {
  cardType: CardType | string | undefined;
  pageWidthPts: number;
  pageHeightPts: number;
  isFoldable: boolean;
  pageOrientation:
    | 'PORTRAIT'
    | 'LANDSCAPE'
    | undefined
    | WProjectCustomizationPageView.OrientationEnum;
  disableBleed?: boolean;
  bleed?: Bleed;
}) => {
  // If the bleed is explicitly specified, use it. Otherwise, calculate it using cardType.
  const cardBleed = bleed || getCardBleed(cardType);
  const verticalBleed = cardBleed.bottom + cardBleed.top;
  const horizontalBleed = cardBleed.left + cardBleed.right;

  /**
   * If "disableBleed" is true, that means that we want to subtract the bleed size
   * from the page's dimensions to clearly show what the printed result will be.
   *
   * This is only needed for card types that include the bleed in their templates (i.e. that are cut after printing),
   * and is typically not done in the customization flow (because we explicitly show the bleed + safety area there).
   */
  const adjustedWidth = disableBleed ? pageWidthPts - horizontalBleed : pageWidthPts;
  const adjustedHeight = disableBleed ? pageHeightPts - verticalBleed : pageHeightPts;

  // Adjust either the height or width if the card is folded
  const [cardWidthPts, cardHeightPts] = getSizesPerOrientation(
    adjustedWidth,
    adjustedHeight,
    isFoldable,
    pageOrientation
  );

  return {
    cardHeightPts,
    cardWidthPts,
  };
};
